'use client';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import {
  BLOCKS,
  Document,
  Heading1,
  Hr,
  INLINES,
  ListItem,
  MARKS,
  OrderedList,
  Paragraph,
  Quote,
  UnorderedList,
} from '@contentful/rich-text-types';
import { motion, useInView } from 'motion/react';
import Image from 'next/image';
import { Link } from 'next-view-transitions';
import * as React from 'react';
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { SvgLinesComponent } from '@/app/components/ui/SvgLinesComponent';
import { cn } from '@/app/lib/utils/cn';
import { getHref } from '@/app/lib/utils/getHref';
import { paddingClasses } from '@/app/lib/utils/getPaddingClasses';
import renderBackground from '@/app/lib/utils/renderBackground';
import type { ContentfulImage } from '@/app/types/contentful/types';

type SpacingKey = keyof typeof paddingClasses.top | keyof typeof paddingClasses.bottom;

type TwoColumnSectionsProps = {
  content: {
    twoColumnSectionsComponent: {
      title: string;
      subtitle: string;
      showGridLines: boolean;
      backgroundType: string;
      spacingTop: SpacingKey;
      spacingBottom: SpacingKey;
      twoColumnItemsCollection: {
        items: {
          sys: {
            id: string;
          };
          featuredImage: ContentfulImage;
          text: Document;
          informationToggleItemsCollection: {
            items: {
              title: string;
              description: string;
            }[];
          };
        }[];
      };
    };
  };
};

export default function TwoColumnSectionsComponent({ content }: TwoColumnSectionsProps) {
  const {
    twoColumnSectionsComponent: { title, subtitle, showGridLines, backgroundType, spacingTop, spacingBottom, twoColumnItemsCollection },
  } = content;

  const paddingTop = paddingClasses.top?.[spacingTop] || 'pt-4';
  const paddingBottom = paddingClasses.bottom?.[spacingBottom] || 'pb-32';

  return (
    <section className="relative col-span-full flex flex-col gap-4" aria-labelledby="section-title">
      {showGridLines && (
        <div className="absolute left-0 top-0 -z-10 h-full w-full" aria-hidden="true">
          <SvgLinesComponent />
        </div>
      )}
      {renderBackground(backgroundType)}

      <div className={`${paddingTop} ${paddingBottom} space-y-25 md-lg:space-y-30`}>
        <div className="flex flex-col items-center text-WHITE md:container md-lg:mb-[20px]">
          <h2 id="section-title" className="max-w-[20ch] text-center text-H1 font-bold md:text-H1-Mobile">
            {title}
          </h2>
          <p className="mt-4 max-w-[40ch] text-center text-Body font-light sm:text-Body">{subtitle}</p>
        </div>

        <div className="flex flex-col gap-20 md:gap-30">
          {twoColumnItemsCollection.items.map((item: any, index: number) => {
            return <TwoColumnSectionItem key={item.sys.id} item={item} sectionIndex={index} />;
          })}
        </div>
      </div>
    </section>
  );
}

function TwoColumnSectionItem({ item, sectionIndex }: { item: any; sectionIndex: number }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const totalItems = item.informationToggleItemsCollection.items.length;
  const toggleItemRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const isInView = useInView(toggleItemRef, { once: false, amount: 0.25 });
  const imageInView = useInView(imageRef, { once: true, amount: 0.25 });

  // State for measured heights and measurement completion
  const [measuredHeights, setMeasuredHeights] = useState<number[]>(Array(totalItems).fill(160)); // Default height
  const [isMeasured, setIsMeasured] = useState(false);

  // State for the indicator and its container
  const [indicatorStyle, setIndicatorStyle] = useState({ transform: 'translateY(0px)', height: '160px' });
  const [indicatorContainerHeight, setIndicatorContainerHeight] = useState('320px'); // Default container height

  // Refs for the actual displayed items and the hidden measurement items
  const itemRefs = useRef<(HTMLDivElement | null)[]>(Array(totalItems).fill(null));
  const measurementItemRefs = useRef<(HTMLDivElement | null)[]>(Array(totalItems).fill(null));

  // Pause/play logic when in view
  useEffect(() => {
    if (isInView) {
      setIsPaused(false);
    } else {
      setIsPaused(true);
    }
  }, [isInView]);

  // Auto-advance logic
  useEffect(() => {
    if (isPaused || !isMeasured) return; // Don't auto-advance if paused or not measured

    const interval = setInterval(() => {
      setActiveIndex(prevIndex => (prevIndex + 1) % totalItems);
    }, 4000);

    return () => clearInterval(interval);
  }, [isPaused, totalItems, isMeasured]);

  // Pre-measurement effect
  useLayoutEffect(() => {
    if (measurementItemRefs.current.some(ref => !ref)) {
      // Wait until all measurement refs are populated
      return;
    }

    const heights = measurementItemRefs.current.map(ref => ref?.getBoundingClientRect().height || 170); // Measure or use default
    setMeasuredHeights(heights);
    setIsMeasured(true);
    // console.log('Measured Heights:', heights);
  }, []); // Run only once on mount

  // Effect to update indicator style and container height based on measurements
  useEffect(() => {
    if (!isMeasured) return; // Only run after measurements are done

    let topOffset = 0;
    let totalHeight = 0;
    const closedHeight = 40; // Assuming a fixed closed height (header + closed content area)

    for (let i = 0; i < totalItems; i++) {
      const currentHeight = i === activeIndex ? measuredHeights[i] : closedHeight;
      if (i < activeIndex) {
        topOffset += currentHeight;
      }
      totalHeight += currentHeight;
    }

    const activeItemHeight = measuredHeights[activeIndex];

    setIndicatorStyle({
      transform: `translateY(${topOffset}px)`,
      height: `${activeItemHeight}px`,
    });
    setIndicatorContainerHeight(`${totalHeight}px`);
    // console.log(`Active Index: ${activeIndex}, Top Offset: ${topOffset}, Active Height: ${activeItemHeight}, Total Height: ${totalHeight}`);
  }, [activeIndex, measuredHeights, isMeasured, totalItems]);

  const options: any = {
    renderNode: {
      [BLOCKS.HEADING_1]: (_node: Heading1, children: any) => (
        <h3 className="text-balance pb-3 text-H1 font-bold leading-[1.15] text-WHITE/90 md:pb-1 md:text-H3">{children}</h3>
      ),
      [BLOCKS.PARAGRAPH]: (_node: Paragraph, children: any) => (
        <p className="text-pretty pb-1 text-Body font-normal text-WHITE/75 md:pt-2">{children}</p>
      ),
      [BLOCKS.QUOTE]: (_node: Quote, children: any) => (
        <blockquote className="text-balance rounded-md bg-slate-700 p-4 py-3 text-Body-Large font-normal text-WHITE">{children}</blockquote>
      ),
      [BLOCKS.HR]: (_node: Hr, _children: any) => <hr className="border-t-2 border-WHITE/20 pb-5 pt-8" />,
      [INLINES.HYPERLINK]: (node: any, children: any) => {
        const href = node.data.uri;
        return (
          <Link href={href} target="_blank" className="text-BLUE-400 underline">
            {children}
          </Link>
        );
      },
      [BLOCKS.UL_LIST]: (_node: UnorderedList, children: any) => (
        <ul className="list-disc pl-5 pt-3 text-Body font-normal text-WHITE">{children}</ul>
      ),
      [BLOCKS.OL_LIST]: (_node: OrderedList, children: any) => (
        <ol className="list-decimal pl-5 pt-3 text-Body font-normal text-WHITE">{children}</ol>
      ),
      [BLOCKS.LIST_ITEM]: (_node: ListItem, children: any) => <li className="mb-2 marker:text-GREEN-500">{children}</li>,
      [INLINES.EMBEDDED_ENTRY]: (node: any) => {
        const entryId = node.data.target.sys.id;
        const entry = item.text.links.entries.inline.find((entry: any) => entry.sys.id === entryId);
        const href = getHref(entry?.route, entry?.slug, entry?.url);

        if (!entry) return null;

        return (
          <Link
            href={href}
            target="_self"
            className="relative text-GREEN-500 underline transition-all duration-300 hover:text-GREEN-500/80"
            style={{ alignContent: 'center' }}
          >
            {/* Span for a increased mobile touch target size. Only visible on touch devices where pointer:coarse */}
            <span className="absolute left-1/2 top-1/2 h-10 w-[calc(100%+20px)] -translate-x-1/2 -translate-y-1/2 [@media(pointer:fine)]:hidden" />
            {entry.label}
          </Link>
        );
      },
    },
    renderMark: {
      [MARKS.BOLD]: (text: ReactNode) => <strong className="font-bold text-WHITE/90">{text}</strong>,
      [MARKS.ITALIC]: (text: ReactNode) => <em className="italic">{text}</em>,
      [MARKS.UNDERLINE]: (text: ReactNode) => <span className="underline">{text}</span>,
      [MARKS.SUPERSCRIPT]: (text: ReactNode) => <sup className="text-xs">{text}</sup>,
      [MARKS.SUBSCRIPT]: (text: ReactNode) => <sub className="text-xs">{text}</sub>,
      [MARKS.STRIKETHROUGH]: (text: ReactNode) => <span className="line-through">{text}</span>,
    },
  };

  return (
    <article
      key={item.sys.id}
      className="grid auto-rows-[minmax(500px,750px)] grid-cols-12 py-10 md-lg:container md-lg:auto-rows-min md-lg:gap-y-20 md-lg:py-0 md:gap-y-15"
      ref={toggleItemRef}
    >
      {/* Column left */}
      <div className="col-span-4 row-start-1 row-end-1 ml-5 flex h-[575px] flex-col gap-y-12 self-center md-lg:col-span-full md-lg:ml-0 md-lg:h-[450px] md:h-[575px] md:justify-evenly sm:gap-y-5">
        <div className="space-y-4">{documentToReactComponents(item.text.json as Document, options)}</div>

        {/* // ? Hidden Measurement Div */}
        <div className="pointer-events-none invisible absolute left-0 top-0 -z-10 select-none opacity-0" aria-hidden="true">
          {item.informationToggleItemsCollection.items.map((toggleItem: any, index: number) => (
            <InformationToggleItem
              key={`measure-${toggleItem.sys.id}-${index}`}
              ref={el => (measurementItemRefs.current[index] = el)}
              toggleItem={toggleItem}
              isActive={true} // Force open for measurement
              forceOpen={true}
              onActivate={() => {}}
              isFirst={index === 0}
              isLast={index === totalItems - 1}
            />
          ))}
        </div>

        {/* Visible Interaction Area */}
        <motion.div
          layout
          className="grid grid-cols-[2px_auto] items-start gap-x-7 md-lg:gap-x-2 sm:h-[250px]"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={() => setIsPaused(true)}
        >
          {/* Progress indicator bar with dynamic container height */}
          <motion.div
            className="relative bg-GREEN-500/20 will-change-transform"
            animate={{ height: indicatorContainerHeight }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} // Animate height change
          >
            <motion.div
              className="absolute left-0 right-0 bg-GREEN-500 will-change-transform"
              style={{ height: indicatorStyle.height }} // Use height from state
              animate={{ transform: indicatorStyle.transform }} // Animate transform
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30,
              }}
            />
          </motion.div>

          {/* Visible Toggle items */}
          <div className="flex w-full flex-col">
            {item.informationToggleItemsCollection.items.map((toggleItem: any, index: number) => (
              <InformationToggleItem
                key={toggleItem.sys.id + index}
                ref={el => (itemRefs.current[index] = el)} // Ref for potentially measuring visible items if needed later
                toggleItem={toggleItem}
                isActive={activeIndex === index}
                onActivate={() => setActiveIndex(index)}
                isFirst={index === 0}
                isLast={index === totalItems - 1}
              />
            ))}
          </div>
        </motion.div>
      </div>

      {/* Column right - image section */}
      <div
        ref={imageRef}
        className={cn(
          'relative isolate col-span-full col-start-7 h-[575px] col-end-13 self-center overflow-clip border-l py-8 px-10 md-lg:col-span-full md-lg:row-start-2 md-lg:row-end-2 md-lg:h-full md-lg:rounded-md md-lg:border md-lg:p-10 md:p-5 sm:p-3',
          {
            'border-GREEN-500': sectionIndex % 3 === 0,
            'border-BLUE': sectionIndex % 3 === 1,
            'border-PURP': sectionIndex % 3 === 2,
          },
        )}
      >
        {!isImageLoaded && <div className="skeleton-loading absolute inset-0 flex items-center justify-center bg-WHITE/10" />}
        <Image
          src={item.featuredImage.url}
          alt={item.featuredImage.description}
          width={item.featuredImage.width}
          height={item.featuredImage.height}
          aria-label={item.featuredImage.title}
          className={cn({
            'h-full w-full object-contain opacity-100 transition-all duration-1000': isImageLoaded && imageInView,
            'scale-95 opacity-0': !isImageLoaded || !imageInView,
          })}
          onLoad={() => setIsImageLoaded(true)}
        />
      </div>
    </article>
  );
}

// First, define proper types for the props
type InformationToggleItemProps = {
  toggleItem: {
    title: string;
    description: string;
  };
  isActive: boolean;
  onActivate: () => void;
  isFirst: boolean;
  isLast: boolean;
  forceOpen?: boolean;
};

// Use React.forwardRef
const InformationToggleItem = React.forwardRef<HTMLDivElement, InformationToggleItemProps>(
  ({ toggleItem, isActive, onActivate, isFirst, isLast, forceOpen }, ref) => {
    // Determine animation state based on forceOpen or isActive
    const animateState = forceOpen ? 'open' : isActive ? 'open' : 'closed';

    return (
      <motion.div
        role="button"
        aria-label={toggleItem.title}
        className={`w-[450px] max-w-full cursor-pointer touch-manipulation overflow-hidden md-lg:w-full ${
          !isFirst ? 'border-t border-GREEN-500/20' : ''
        } ${isLast ? '' : ''}`}
        layout
        onMouseEnter={onActivate}
        onTouchStart={onActivate}
        ref={ref}
      >
        <div className="flex h-10 items-center justify-between">
          <motion.h3
            className={`text-Body font-bold ${isActive ? 'text-WHITE/95' : 'text-WHITE/50'} pl-1`}
            layout
            transition={{ duration: 0.2 }}
          >
            {toggleItem.title}
          </motion.h3>

          {/* Add chevron indicator */}
          <motion.div animate={{ rotate: isActive ? 180 : 0 }} transition={{ duration: 0.3 }} className="mr-2">
            <svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M1 1L6 6L11 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={isActive ? 'text-WHITE' : 'text-WHITE/50'}
              />
            </svg>
          </motion.div>
        </div>

        <motion.div
          initial={false}
          aria-hidden={animateState === 'closed'}
          aria-expanded={animateState === 'open'}
          variants={{
            open: {
              height: 'auto',
              maskImage: 'linear-gradient(to bottom, black 100%, transparent 100%)',
            },
            closed: {
              height: '0px',
              maskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
            },
          }}
          animate={animateState}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <motion.div className="px-1 pb-2">
            <p className="text-Body font-normal text-WHITE/80">{toggleItem.description}</p>
          </motion.div>
        </motion.div>
      </motion.div>
    );
  },
);

InformationToggleItem.displayName = 'InformationToggleItem';
